// Deck recipe category — lists templates in one taxonomy bucket with category SEO.
//
// Header (sidebar + back + “Open deck”): `getDeckRecipesCategoryRouteNavigationOptions`
// in `DeckRecipesNavigationOptions.jsx`.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { View, ScrollView, StyleSheet, Pressable, Platform, TextInput } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { Redirect, useLocalSearchParams, useRouter } from 'expo-router';
import { useTheme } from '@/context/ThemeContext';
import useResponsiveness from '@/hooks/useResponsiveness';
import useRequireAuth from '@/hooks/useRequireAuth';
import { deckTemplatesApi, deckTemplateCategoriesApi } from '@/lib/api';
import { useDeckStore } from '@/store';
import MetaHead from '@/components/MetaHead';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import { BORDER_RADIUS } from '@/constants/config';
import DeckRecipeCard from '@/components/Deck/DeckRecipeCard';
import DeckTemplateFramePreview from '@/components/Deck/DeckTemplateFramePreview';
import ToolPageCopy from '@/components/Tools/ToolPageCopy';
import AppDashboardFooter from '@/components/AppDashboardFooter';
import CatalogDetailHero from '@/components/Catalog/CatalogDetailHero';
import { heroChromaFromPaletteFg, accentHexFromPaletteFg } from '@/components/Tools/toolDetailHelpers';
import { buildCategoryLookupMaps, enrichTemplateCategory, recipeCategorySlug } from '@/utils/deckRecipeCategory';

const isWeb = Platform.OS === 'web';
const IS_TOUCH_NATIVE = Platform.OS === 'ios' || Platform.OS === 'android';

/**
 * Convert seeded `pageCopy` blocks (mix of new-style `section`/`ordered-list`/
 * `unordered-list`/`cards`/`faq` and legacy `h2`/`p`) into a flat list of
 * blocks the `ToolPageCopy` renderer understands. Adjacent `h2` + `p` runs
 * fold into a single `section` block.
 */
function normalizeCategoryPageCopy(blocks) {
  if (!Array.isArray(blocks)) return [];
  const supported = new Set(['section', 'ordered-list', 'unordered-list', 'cards', 'faq', 'callout']);
  const isLegacy = blocks.some((b) => b?.type === 'h2' || b?.type === 'p');
  if (!isLegacy) return blocks.filter((b) => b && supported.has(b.type));

  const out = [];
  for (let i = 0; i < blocks.length; i++) {
    const b = blocks[i];
    if (!b) continue;
    if (supported.has(b.type)) { out.push(b); continue; }
    if (b.type === 'h2') {
      const title = b.text || '';
      const paragraphs = [];
      let j = i + 1;
      while (j < blocks.length && blocks[j]?.type === 'p' && blocks[j]?.text) {
        paragraphs.push(blocks[j].text);
        j += 1;
      }
      out.push({ type: 'section', title, copy: paragraphs.join('\n\n') });
      i = j - 1;
    } else if (b.type === 'p' && b.text) {
      out.push({ type: 'section', copy: b.text });
    }
  }
  return out;
}

function buildCategoryFaqBlock(faq, title) {
  if (!Array.isArray(faq) || faq.length === 0) return null;
  const items = faq
    .map((row) => {
      if (!row) return null;
      const question = row.question || row.q || row.title;
      const answer = row.answer || row.a || row.copy || row.description;
      if (!question || !answer) return null;
      return { question, answer };
    })
    .filter(Boolean);
  if (items.length === 0) return null;
  return { type: 'faq', title: title || 'Frequently asked questions', items };
}

// The phone apps don't ship Deck, so its recipe pages have nowhere to lead.
export default function DeckRecipeCategoryRoute() {
  if (IS_TOUCH_NATIVE) return <Redirect href="/" />;
  return <DeckRecipeCategoryPage />;
}

function DeckRecipeCategoryPage() {
  const { colors } = useTheme();
  const { isXS, isSM, isMD, windowWidth } = useResponsiveness();
  const router = useRouter();
  const params = useLocalSearchParams();
  const slug = typeof params?.slug === 'string' ? params.slug : (Array.isArray(params?.slug) ? params.slug[0] : '');
  const slugDisplayName = useMemo(
    () => (slug ? slug.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : ''),
    [slug],
  );

  const createDeck = useDeckStore(state => state.actions.createDeck);
  const requireAuth = useRequireAuth();

  const [category, setCategory] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busySlug, setBusySlug] = useState(null);
  const useRecipeBusyRef = useRef(false);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    let alive = true;
    if (!slug) {
      setCategory(null);
      setTemplates([]);
      setCategories([]);
      setError(null);
      setSearchQuery('');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const [catData, tplData, allCats] = await Promise.all([
          deckTemplateCategoriesApi.getBySlug(slug).catch(() => ({ category: null })),
          deckTemplatesApi.list({ category: slug, limit: 200 }),
          deckTemplateCategoriesApi.list().catch(() => ({ categories: [] })),
        ]);
        if (!alive) return;
        setCategory(catData?.category || null);
        const cats = Array.isArray(allCats?.categories) ? allCats.categories : [];
        setCategories(cats);

        let list = Array.isArray(tplData?.templates) ? tplData.templates : [];
        const slugKey = String(slug).toLowerCase();
        if (list.length === 0 && slugKey) {
          const allTpl = await deckTemplatesApi.list({ limit: 250 });
          const all = Array.isArray(allTpl?.templates) ? allTpl.templates : [];
          const maps = buildCategoryLookupMaps(cats);
          list = all.filter((t) => recipeCategorySlug(enrichTemplateCategory(t, maps), slugKey) === slugKey);
        }
        setTemplates(list);
      } catch (err) {
        if (!alive) return;
        setError(err?.message || 'Failed to load category');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [slug]);

  const maps = useMemo(() => buildCategoryLookupMaps(categories), [categories]);

  const enrichedTemplates = useMemo(
    () => templates.map((t) => enrichTemplateCategory(t, maps)),
    [templates, maps],
  );

  /** First recipe in this category — drives the deck chrome preview above search. */
  const heroTemplate = enrichedTemplates[0] || null;

  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return enrichedTemplates;
    return enrichedTemplates.filter((t) => {
      const hay = [
        t.name,
        t.description,
        t.category?.name,
        t.category?.slug,
        t.slug,
        ...(Array.isArray(t.columns) ? t.columns.map((c) => [c.label, c.modality, c.type].filter(Boolean).join(' ')) : []),
      ]
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [enrichedTemplates, searchQuery]);

  const { cardGridGap, cardColWidth } = useMemo(() => {
    if (isXS) return { cardGridGap: 12, cardColWidth: Math.min(300, Math.max(248, windowWidth - 48)) };
    if (isSM) return { cardGridGap: 14, cardColWidth: 252 };
    if (isMD) return { cardGridGap: 14, cardColWidth: 264 };
    return { cardGridGap: 16, cardColWidth: 272 };
  }, [isXS, isSM, isMD, windowWidth]);

  const cardRowStyle = useMemo(() => {
    if (Platform.OS !== 'web') {
      return [styles.cardRow, { gap: cardGridGap }];
    }
    return [
      styles.cardRow,
      styles.cardRowWeb,
      {
        gap: cardGridGap,
        gridTemplateColumns: `repeat(auto-fill, minmax(${Math.round(cardColWidth)}px, 1fr))`,
      },
    ];
  }, [cardGridGap, cardColWidth]);

  const scrollPaddingX = isXS ? 16 : isSM ? 20 : 28;

  const handleUseRecipe = useCallback(async (template) => {
    if (!template?.slug || useRecipeBusyRef.current) return;
    // Creating a deck is owner-only; surface the unified auth CTA for
    // anonymous visitors instead of bouncing them to the detail page.
    const ran = requireAuth(() => true, {
      source: 'deck_recipe_category_use_recipe',
      title: `Sign in to use ${template?.name || 'this recipe'}`,
      subtitle: 'Recipes turn into decks in your library so you can keep iterating on them.',
      icon: 'layer-group',
    });
    if (ran !== true) return;
    useRecipeBusyRef.current = true;
    setBusySlug(template.slug);
    try {
      const deckSlug = await createDeck({ name: template.name, templateSlug: template.slug });
      if (deckSlug) {
        router.push({ pathname: '/deck', params: { d: deckSlug } });
      }
    } finally {
      useRecipeBusyRef.current = false;
      setBusySlug(null);
    }
  }, [requireAuth, createDeck, router]);

  const handleCardPress = useCallback((template) => {
    router.push(`/decks/recipes/${template.slug}`);
  }, [router]);

  const metaTitle = useMemo(
    () => {
      if (!slug) return 'Deck Recipes | Gab AI';
      return category?.meta?.title || `${category?.name || slugDisplayName || slug} — Deck Recipes | Gab AI`;
    },
    [category, slugDisplayName, slug],
  );

  const metaDesc = useMemo(
    () => category?.meta?.description || category?.description || '',
    [category],
  );

  // ── Long-form on-page content (rendered via ToolPageCopy) ─────────────
  const pageCopyBlocks = useMemo(() => {
    const blocks = normalizeCategoryPageCopy(category?.meta?.pageCopy);
    const hasFaqBlock = blocks.some((b) => b?.type === 'faq');
    if (hasFaqBlock) return blocks;
    const faqTitle = category?.name ? `${category.name} recipes FAQ` : 'Frequently asked questions';
    const faqBlock = buildCategoryFaqBlock(category?.meta?.faq, faqTitle);
    return faqBlock ? [...blocks, faqBlock] : blocks;
  }, [category]);

  const pageCopyPalette = useMemo(() => {
    const fg = colors.text_color_primary;
    return { fg, bg: `${fg}1f`, soft: `${fg}0d` };
  }, [colors.text_color_primary]);

  const categoryHeroPalette = useMemo(() => {
    const fg = colors.accent_color_primary;
    return { fg, bg: `${fg}1f`, soft: `${fg}0d` };
  }, [colors.accent_color_primary]);

  // Hero wash chroma + solid accent hex — same values
  // `CatalogDetailHero` computes from the palette, so the bottom CTA's
  // tint reads identically to the page hero.
  const heroChroma = useMemo(
    () => heroChromaFromPaletteFg(categoryHeroPalette.fg),
    [categoryHeroPalette.fg],
  );
  const accentHex = useMemo(
    () => accentHexFromPaletteFg(categoryHeroPalette.fg) || colors.accent_color_primary,
    [categoryHeroPalette.fg, colors.accent_color_primary],
  );

  const categoryHeroMetaItems = useMemo(
    () => [{ icon: 'layer-group', label: `${enrichedTemplates.length} recipe${enrichedTemplates.length === 1 ? '' : 's'}` }],
    [enrichedTemplates.length],
  );

  const structuredData = useMemo(() => {
    if (!category) return null;
    const url = `https://gab.ai/decks/recipes/category/${slug}`;
    const graph = [
      {
        '@type': 'CollectionPage',
        name: metaTitle,
        description: metaDesc,
        url,
        isPartOf: { '@type': 'WebSite', name: 'Gab AI', url: 'https://gab.ai' },
        inLanguage: 'en-US',
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://gab.ai' },
          { '@type': 'ListItem', position: 2, name: 'Deck', item: 'https://gab.ai/deck' },
          { '@type': 'ListItem', position: 3, name: 'Deck Recipes', item: 'https://gab.ai/decks/recipes' },
          { '@type': 'ListItem', position: 4, name: category?.name || slugDisplayName || slug, item: url },
        ],
      },
    ];
    if (enrichedTemplates.length > 0) {
      graph.push({
        '@type': 'ItemList',
        name: `${category?.name || slugDisplayName || slug} deck recipes`,
        numberOfItems: enrichedTemplates.length,
        itemListElement: enrichedTemplates.slice(0, 100).map((t, i) => ({
          '@type': 'ListItem',
          position: i + 1,
          url: `https://gab.ai/decks/recipes/${t.slug}`,
          name: t.name,
        })),
      });
    }
    const faqItems = Array.isArray(category?.meta?.faq) ? category.meta.faq : [];
    if (faqItems.length > 0) {
      graph.push({
        '@type': 'FAQPage',
        mainEntity: faqItems
          .map((row) => {
            const q = row?.q || row?.question;
            const a = row?.a || row?.answer;
            return q && a ? { '@type': 'Question', name: q, acceptedAnswer: { '@type': 'Answer', text: a } } : null;
          })
          .filter(Boolean),
      });
    }
    return { '@context': 'https://schema.org', '@graph': graph };
  }, [category, enrichedTemplates, slug, slugDisplayName, metaTitle, metaDesc]);

  return (
    <>
      <MetaHead
        title={metaTitle}
        description={metaDesc}
        canonical={`/decks/recipes/category/${slug}`}
        keywords={category?.meta?.keywords || ''}
        structuredData={structuredData || undefined}
        type="website"
      />

      <ScrollView
        style={{ flex: 1, backgroundColor: colors.solid_color_primary }}
        contentContainerStyle={styles.scroll}
      >
        {loading && (
          <View style={{ width: '100%', alignItems: 'center', paddingHorizontal: scrollPaddingX, paddingTop: 28 }}>
            <View style={[styles.wrap, { width: '100%' }]}>
              <View style={styles.loading}>
                <Icon name="spinner" size={18} color={colors.text_color_tertiary} />
              </View>
            </View>
          </View>
        )}

        {!!error && !loading && (
          <View style={{ width: '100%', alignItems: 'center', paddingHorizontal: scrollPaddingX, paddingTop: 28 }}>
            <View style={styles.wrap}>
              <View style={[styles.errorBox, { backgroundColor: colors.solid_color_block, borderColor: colors.border_color_secondary }]}>
                <Text size="s" color="secondary">{error}</Text>
              </View>
            </View>
          </View>
        )}

        {!loading && !error && (
          <>
            <CatalogDetailHero
              backLink={!IS_TOUCH_NATIVE ? {
                label: 'All recipes',
                onPress: () => router.push('/decks/recipes'),
              } : null}
              palette={categoryHeroPalette}
              categorySlug={slug || undefined}
              catalogCategory={{
                label: 'Deck recipes',
                icon: 'table-columns',
              }}
              heroMaxWidth={1180}
              headline={category?.name || slugDisplayName}
              subtitle={category?.description?.trim() || null}
              heroIcon="layer-group"
              metaItems={categoryHeroMetaItems}
            />

            <View style={[styles.wrap, { paddingHorizontal: scrollPaddingX }]}>
              <View
                style={[
                  styles.deckExplainer,
                  { borderColor: colors.border_color_secondary, backgroundColor: colors.solid_color_block },
                ]}
              >
                <View style={styles.deckExplainerRow}>
                  <View style={[styles.deckExplainerIcon, { backgroundColor: 'rgba(59,130,246,0.12)' }]}>
                    <Icon name="table-columns" size={14} color="#3b82f6" solid />
                  </View>
                  <View style={{ flex: 1, minWidth: 0 }}>
                    <Text size="xxs" weight="bold" color="tertiary" style={styles.deckExplainerEyebrow}>
                      What’s a deck?
                    </Text>
                    <Text size="s" color="secondary" style={styles.deckExplainerBody}>
                      A deck is Gab AI’s{' '}
                      <Text weight="bold" color="primary">multi-column workspace</Text>
                      {' '}— not one long chat. Each column runs a different model or tool (text, image, video, music, voice, transcription, one-click tools, and more) side by side, all reading the same brief.{' '}
                      <Text weight="bold" color="primary">Recipes</Text>
                      {' '}are pre-wired layouts you clone in one click; rename columns, swap models, or add up to 12 columns whenever you want.
                    </Text>
                  </View>
                </View>
              </View>

              {heroTemplate && Array.isArray(heroTemplate.columns) && heroTemplate.columns.length > 0 && (
                <View style={styles.deckPreviewWrap}>
                  <DeckTemplateFramePreview
                    template={heroTemplate}
                    isXS={isXS}
                    onPress={() => handleCardPress(heroTemplate)}
                  />
                </View>
              )}

              {filtered.length > 0 && (
                <View
                  style={[
                    styles.searchShell,
                    { borderColor: colors.border_color_secondary, backgroundColor: colors.solid_color_block },
                  ]}
                >
                  <Icon name="magnifying-glass" size={14} color={colors.text_color_tertiary} />
                  <TextInput
                    value={searchQuery}
                    onChangeText={setSearchQuery}
                    placeholder="Search in this category…"
                    placeholderTextColor={colors.text_color_tertiary}
                    style={[styles.searchInput, { color: colors.text_color_primary }]}
                    autoCorrect={false}
                    autoCapitalize="none"
                    {...(Platform.OS === 'web' ? { outlineStyle: 'none' } : {})}
                  />
                </View>
              )}

              <Text size="xs" weight="bold" color="tertiary" style={[styles.sectionLabel, isXS && styles.sectionLabelTight]}>
                {filtered.length} recipe{filtered.length === 1 ? '' : 's'}
              </Text>

              {filtered.length === 0 ? (
                <View style={[styles.emptyBox, { backgroundColor: colors.solid_color_block, borderColor: colors.border_color_secondary }]}>
                  <Icon name="layer-group" size={20} color={colors.text_color_tertiary} />
                  <Text size="m" weight="bold" color="primary" style={{ marginTop: 12 }}>
                    {searchQuery.trim() ? 'No recipes match your search' : 'No recipes in this category yet'}
                  </Text>
                  <Text size="s" color="secondary" style={{ marginTop: 4, textAlign: 'center' }}>
                    {searchQuery.trim()
                      ? 'Try a different keyword, or browse all recipes.'
                      : 'New deck recipes are added regularly. Check back soon, or browse other categories.'}
                  </Text>
                  <Pressable
                    onPress={() => router.push('/decks/recipes')}
                    style={({ hovered }) => [
                      styles.emptyCta,
                      { borderColor: colors.border_color_secondary, backgroundColor: hovered ? colors.solid_color_hover : 'transparent' },
                    ]}
                  >
                    <Text size="xs" weight="bold" color="secondary">Browse all recipes</Text>
                    <Icon name="arrow-right" size={10} color={colors.text_color_secondary} />
                  </Pressable>
                </View>
              ) : (
                <View style={cardRowStyle}>
                  {filtered.map((t) => (
                    <DeckRecipeCard
                      key={t.id || t.slug}
                      template={t}
                      categoryFallbackSlug={slug}
                      layout={{ isXS, isSM, isMD }}
                      onPress={() => handleCardPress(t)}
                      onUse={() => handleUseRecipe(t)}
                      busy={busySlug === t.slug}
                    />
                  ))}
                </View>
              )}

              {/* Editor-written long-form SEO content for this category.
                  Backed by `DeckTemplateCategory.meta.pageCopy` (typed blocks)
                  + `meta.faq`. Renders nothing if neither is seeded. Hidden
                  while the user is searching so card results stay primary. */}
              {!searchQuery.trim() && pageCopyBlocks.length > 0 && (
                <View style={styles.pageCopyWrap}>
                  <ToolPageCopy blocks={pageCopyBlocks} palette={pageCopyPalette} isMD={isMD || isSM} />
                </View>
              )}

              {/* Final CTA — centered hero-language card with a soft
                  accent wash matching `CatalogDetailHero` chroma. Two
                  actions: primary "Start a blank deck" pill button, and
                  a quieter "All recipes" text link below. */}
              {!searchQuery.trim() && (
                <View
                  style={[
                    styles.bottomCta,
                    {
                      borderColor: colors.border_color_secondary,
                      backgroundColor: colors.solid_color_block,
                    },
                  ]}
                >
                  <LinearGradient
                    colors={[colors.solid_color_tertiary, 'transparent']}
                    locations={[0, 1]}
                    start={{ x: 0.5, y: 1 }}
                    end={{ x: 0.5, y: 0.2 }}
                    style={styles.bottomCtaWashSoft}
                    pointerEvents="none"
                  />
                  <LinearGradient
                    colors={[heroChroma.gradSoft, heroChroma.gradMid, 'transparent']}
                    locations={[0, 0.2, 1]}
                    start={{ x: 0, y: 0 }}
                    end={{ x: 1, y: 1 }}
                    style={styles.bottomCtaWashSoft}
                    pointerEvents="none"
                  />
                  <View
                    style={[
                      styles.bottomCtaBlobA,
                      isXS && styles.bottomCtaBlobAMobile,
                      { backgroundColor: heroChroma.blobA },
                    ]}
                    pointerEvents="none"
                  />
                  <View
                    style={[
                      styles.bottomCtaBlobB,
                      isXS && styles.bottomCtaBlobBMobile,
                      { backgroundColor: heroChroma.blobB },
                    ]}
                    pointerEvents="none"
                  />
                  <View style={[styles.bottomCtaInner, isXS && styles.bottomCtaInnerMobile]}>
                    <View
                      style={[
                        styles.bottomCtaBadge,
                        {
                          borderColor: `${accentHex}55`,
                          backgroundColor: `${accentHex}1a`,
                        },
                      ]}
                    >
                      <Icon name="layer-group" size={11} color={accentHex} solid />
                      <Text
                        size="s"
                        weight="extraBold"
                        style={{ color: accentHex, letterSpacing: 0.6 }}
                      >
                        {String(category?.name || slugDisplayName || 'Deck recipes').toUpperCase()}
                      </Text>
                    </View>
                    <Text
                      tag="h2"
                      color="primary"
                      style={[styles.bottomCtaTitle, isXS && styles.bottomCtaTitleMobile]}
                    >
                      Don&rsquo;t see the {category?.name || slugDisplayName} recipe you need?
                    </Text>
                    <Text
                      color="secondary"
                      style={[styles.bottomCtaBody, isXS && styles.bottomCtaBodyMobile]}
                    >
                      Start blank from /deck and build the layout you want — every working deck can be saved back as a recipe under your account.
                    </Text>
                    <Pressable
                      onPress={() => router.push('/deck')}
                      accessibilityRole="button"
                      accessibilityLabel="Start a blank deck"
                      style={({ hovered, pressed }) => [
                        styles.bottomCtaButton,
                        { backgroundColor: accentHex },
                        // Avoid `transform: translateY` on hover — RN Web
                        // can flicker its hover state on the shifted bounds
                        // and trip React's "Maximum update depth" guard.
                        Platform.OS === 'web' && hovered && {
                          boxShadow: '0 10px 28px rgba(0,0,0,0.18)',
                        },
                        pressed && { opacity: 0.92 },
                      ]}
                    >
                      <Icon name="plus" size={16} color="#fff" solid />
                      <Text size="m" weight="bold" style={{ color: '#fff' }}>
                        Start a blank deck
                      </Text>
                      <Icon name="arrow-right" size={12} color="#fff" solid />
                    </Pressable>
                    <Pressable
                      onPress={() => router.push('/decks/recipes')}
                      accessibilityRole="link"
                      accessibilityLabel="All recipes"
                      style={({ hovered }) => [
                        styles.bottomCtaLink,
                        Platform.OS === 'web' && hovered && { opacity: 0.7 },
                      ]}
                    >
                      <Text size="s" weight="semibold" color="secondary">
                        All recipes
                      </Text>
                      <Icon name="arrow-right" size={10} color={colors.text_color_secondary} solid />
                    </Pressable>
                  </View>
                </View>
              )}

              <AppDashboardFooter />
            </View>
          </>
        )}
      </ScrollView>
    </>
  );
}

const styles = StyleSheet.create({
  scroll: {
    flexGrow: 1,
    paddingBottom: 80,
    alignItems: 'center',
    width: '100%',
  },
  wrap: { width: '100%', maxWidth: 1180 },
  deckExplainer: {
    marginTop: 24,
    padding: 14,
    borderRadius: BORDER_RADIUS.medium,
    borderWidth: 1,
    maxWidth: 760,
    alignSelf: 'stretch',
  },
  deckExplainerRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
  },
  deckExplainerIcon: {
    width: 36,
    height: 36,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  deckExplainerEyebrow: {
    letterSpacing: 1.1,
    textTransform: 'uppercase',
    marginBottom: 6,
  },
  deckExplainerBody: {
    lineHeight: 22,
  },
  deckPreviewWrap: {
    width: '100%',
    marginTop: 22,
    marginBottom: 20,
  },
  loading: { marginTop: 48, alignItems: 'center' },
  errorBox: { marginTop: 24, padding: 16, borderRadius: BORDER_RADIUS.medium, borderWidth: 1 },
  sectionLabel: { textTransform: 'uppercase', letterSpacing: 1.2, marginTop: 28, marginBottom: 14 },
  sectionLabelTight: { marginTop: 22, marginBottom: 12 },
  searchShell: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    width: '100%',
    alignSelf: 'stretch',
    marginTop: 20,
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderRadius: BORDER_RADIUS.medium,
    borderWidth: 1,
  },
  searchInput: {
    flex: 1,
    minWidth: 0,
    fontSize: 14,
    paddingVertical: 2,
  },
  cardRow: {
    width: '100%',
    gap: 16,
    ...(Platform.OS === 'web' ? {} : { flexDirection: 'row', flexWrap: 'wrap' }),
  },
  cardRowWeb: {
    display: 'grid',
    alignItems: 'stretch',
    justifyContent: 'start',
  },
  emptyBox: {
    marginTop: 8,
    padding: 32,
    borderRadius: BORDER_RADIUS.medium,
    borderWidth: 1,
    alignItems: 'center',
    width: '100%',
    maxWidth: 520,
  },
  emptyCta: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 6,
    borderWidth: 1,
    marginTop: 16,
    ...(isWeb ? { cursor: 'pointer' } : {}),
  },

  pageCopyWrap: {
    marginTop: 56,
    paddingTop: 8,
    width: '100%',
    maxWidth: 900,
    alignSelf: 'flex-start',
  },
  // "Don't see the X recipe you need?" — centered hero-language CTA
  // card. Per-page chroma/colors are applied inline so this static
  // StyleSheet stays cheap; mobile variants override size/padding.
  bottomCta: {
    marginTop: 56,
    width: '100%',
    borderRadius: BORDER_RADIUS.large,
    borderWidth: 1,
    overflow: 'hidden',
    position: 'relative',
  },
  bottomCtaWashSoft: { ...StyleSheet.absoluteFillObject },
  bottomCtaBlobA: {
    position: 'absolute',
    width: 360,
    height: 360,
    borderRadius: 400,
    top: -118,
    right: -88,
    opacity: isWeb ? 0.52 : 0.5,
    ...(isWeb ? { filter: 'blur(44px)' } : null),
  },
  bottomCtaBlobAMobile: {
    width: 280,
    height: 280,
    top: -100,
    right: -72,
  },
  bottomCtaBlobB: {
    position: 'absolute',
    width: 260,
    height: 260,
    borderRadius: 300,
    bottom: -130,
    left: -90,
    opacity: isWeb ? 0.38 : 0.36,
    ...(isWeb ? { filter: 'blur(36px)' } : null),
  },
  bottomCtaBlobBMobile: {
    width: 200,
    height: 200,
    bottom: -110,
    left: -70,
  },
  bottomCtaInner: {
    padding: 44,
    gap: 12,
    alignItems: 'center',
  },
  bottomCtaInnerMobile: {
    padding: 28,
  },
  bottomCtaBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingVertical: 5,
    paddingHorizontal: 11,
    borderRadius: 999,
    borderWidth: 1,
    marginBottom: 6,
  },
  bottomCtaTitle: {
    fontSize: 34,
    lineHeight: 40,
    letterSpacing: -0.5,
    fontWeight: '800',
    textAlign: 'center',
    marginBottom: 0,
    maxWidth: 720,
  },
  bottomCtaTitleMobile: {
    fontSize: 24,
    lineHeight: 30,
  },
  bottomCtaBody: {
    fontSize: 16,
    lineHeight: 24,
    textAlign: 'center',
    maxWidth: 580,
    opacity: 0.78,
  },
  bottomCtaBodyMobile: {
    fontSize: 15,
    lineHeight: 22,
  },
  bottomCtaButton: {
    marginTop: 14,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 14,
    paddingHorizontal: 26,
    borderRadius: 999,
    ...(isWeb
      ? {
          cursor: 'pointer',
          boxShadow: '0 6px 18px rgba(0,0,0,0.12)',
          transition: 'box-shadow 0.2s ease, background-color 0.15s ease',
        }
      : null),
  },
  bottomCtaLink: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingVertical: 6,
    paddingHorizontal: 4,
    marginTop: 2,
    ...(isWeb ? { cursor: 'pointer', transition: 'opacity 0.15s ease' } : {}),
  },
});
